// src/components/ParametresLogistique.jsx
// ============================================================================
// Paramétrage de la logistique  (Outils administratifs → Paramétrage)
// ============================================================================
//
// Ce que l'école peut régler du module Logistique, rangé comme les autres
// modules (décision de Daniel du 03/11/2026) : ce qui était écrit en dur, et
// les réglages des inventaires qui vivaient dans l'onglet du module.
//
//   Parc                 photos exigées geste par geste, format d'étiquette
//   Catégories et lieux  les listes des fiches d'actif
//   Inventaires          rythme, photo à un sur N, durée
//   Bus scolaires        circuit plein, téléphone du parent, cartes de bus
//
// Chaque onglet suit la licence de sa partie (module.logistique_parc,
// module.logistique_transport) ; les droits d'écrire sont ceux du module,
// vérifiés par le serveur. Les prix du bus restent dans Paramétrage de la
// scolarité (socle).
// ============================================================================

import React, { useState } from 'react';
import { Bus, Camera, ClipboardCheck, ListTree, Truck } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useFeatures } from '../contexts/FeaturesContext';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import ReglagesParc from './logistique/parametrage/ReglagesParc';
import ListesParc from './logistique/parametrage/ListesParc';
import ReglagesInventaires from './logistique/parametrage/ReglagesInventaires';
import ReglagesBus from './logistique/parametrage/ReglagesBus';

const ONGLETS = [
  { cle: 'parc', libelle: 'Parc', icone: Camera, accent: '#246990', licence: 'module.logistique_parc', Composant: ReglagesParc },
  { cle: 'listes', libelle: 'Catégories et lieux', icone: ListTree, accent: '#319190', licence: 'module.logistique_parc', Composant: ListesParc },
  { cle: 'inventaires', libelle: 'Inventaires', icone: ClipboardCheck, accent: '#ae7467', licence: 'module.logistique_parc', Composant: ReglagesInventaires },
  { cle: 'bus', libelle: 'Bus scolaires', icone: Bus, accent: '#7c3aed', licence: 'module.logistique_transport', Composant: ReglagesBus }
];

const COLONNES = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' };

const ParametresLogistique = ({ onBackToDashboard }) => {
  const { estActive } = useFeatures();
  const ouverts = ONGLETS.filter((o) => estActive(o.licence));
  const [onglet, setOnglet] = useState(ouverts[0]?.cle || 'parc');

  const sousTitre = ouverts.map((o) => o.libelle.toLowerCase()).join(', ');
  const enTete = (
    <EnTeteParametrage
      onRetour={onBackToDashboard}
      icone={Truck}
      titre="Paramétrage de la logistique"
      sousTitre={sousTitre ? sousTitre.charAt(0).toUpperCase() + sousTitre.slice(1) : 'Aucune partie de la logistique dans la licence'}
    />
  );

  // Une seule partie : pas d'onglets (PARAMETRAGE-MODULES.md §2).
  if (ouverts.length <= 1) {
    const Seul = ouverts[0]?.Composant;
    return (
      <div className="w-full">
        {enTete}
        <div className="rounded-xl border border-gray-200/80 bg-white p-6 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
          {Seul ? <Seul /> : <p className="text-sm text-gray-500">La licence de l'établissement ne comprend ni le parc ni les bus scolaires.</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {enTete}
      <Tabs value={onglet} onValueChange={setOnglet} className="w-full">
        <TabsList className={`mb-6 grid ${COLONNES[ouverts.length]} rounded-xl bg-[#246990]/8 p-1`}>
          {ouverts.map((o) => (
            <TabsTrigger
              key={o.cle}
              value={o.cle}
              className="rounded-lg text-sm font-semibold text-gray-600 transition-all data-[state=active]:text-white data-[state=active]:shadow-sm"
              style={onglet === o.cle ? { backgroundColor: o.accent } : undefined}
            >
              <o.icone size={15} className="mr-2" />
              <span>{o.libelle}</span>
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="rounded-xl border border-gray-200/80 bg-white p-6 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
          {ouverts.map(({ cle, Composant }) => (
            <TabsContent key={cle} value={cle} className="mt-0 focus-visible:outline-none">
              {onglet === cle && <Composant />}
            </TabsContent>
          ))}
        </div>
      </Tabs>
    </div>
  );
};

export default ParametresLogistique;
